// Propagate a dataset type's schema to the datasets created from it.
//
// Editing a type doesn't rewrite existing datasets server-side, so this walks the client datasets,
// finds the ones bound to the type and PUTs a merged `metadata_schema` that adds any fields the
// dataset is missing while keeping its own customisations.
import { getClients } from "./clients";
import { getDataSets, saveDataSetSchema } from "./data";
import { datasetUsesType, mergeMetadataSchemas } from "../utils/datasetTypes";

/** Every dataset (across clients) bound to the given type. */
export const findDatasetsForType = async (type) => {
  const clientsResult = await getClients();
  const clients = Array.isArray(clientsResult) ? clientsResult : clientsResult?.clients ?? [];

  const datasets = [];

  for (const client of clients) {
    const clientId = client?.id ?? client?.public_id;
    if (!clientId) continue;

    // Sequential on purpose: an admin action, and it keeps the request burst small.
    // eslint-disable-next-line no-await-in-loop
    const result = await getDataSets(clientId);
    const list = Array.isArray(result) ? result : result?.data_sets ?? [];

    list.forEach((dataset) => {
      if (datasetUsesType(dataset, type)) datasets.push(dataset);
    });
  }

  return datasets;
};

/**
 * @param {object} type The type being changed (needs `id` / `public_id` / `name`).
 * @param {object} schema Its new `metadata_schema`.
 * @returns {Promise<{total: number, updated: number, unchanged: number, failed: object[]}>}
 */
export const cascadeTypeSchema = async (type, schema) => {
  const datasets = await findDatasetsForType(type);
  const summary = { total: datasets.length, updated: 0, unchanged: 0, failed: [] };

  for (const dataset of datasets) {
    if (!dataset?.public_id) continue;

    const merged = mergeMetadataSchemas(dataset.metadata_schema ?? null, schema);

    if (JSON.stringify(merged ?? null) === JSON.stringify(dataset.metadata_schema ?? null)) {
      summary.unchanged += 1;
      continue;
    }

    // eslint-disable-next-line no-await-in-loop
    const result = await saveDataSetSchema(dataset.public_id, merged);

    if (result?.success === false) {
      summary.failed.push({ name: dataset.name ?? dataset.public_id, message: result.message });
    } else {
      summary.updated += 1;
    }
  }

  return summary;
};

export default cascadeTypeSchema;
